import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { FieldError } from "~/components/field-error";
import { api, APIError } from "~/lib/api";
import { formatDate, formatKES, toNum } from "~/lib/format";
import type { IParcel, ITransaction } from "~/lib/proto";
import { cn } from "~/lib/utils";

const FEE_EDITABLE = ["REGISTERED", "RECEIVED_ABROAD"];
const RECENT_PAYMENTS = 3;

function useRefreshParcel(parcelId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["ops", "parcels"] });
    void qc.invalidateQueries({ queryKey: ["parcel", parcelId] });
  };
}

export function ParcelMoney({ parcel }: { parcel: IParcel }) {
  const status = parcel.status ?? "";
  const total = toNum(parcel.amountChargedKes);
  const fee = toNum(parcel.deliveryFeeKes);
  const charges = parcel.charges ?? [];
  const shipping = total - fee - charges.reduce((sum, charge) => sum + toNum(charge.amountKes), 0);
  const priced = total > 0;
  const settled = ["PAID", "OUT_FOR_DELIVERY", "DELIVERED"].includes(status);

  return (
    <div className="space-y-3">
      {priced ? (
        <dl className="space-y-1.5 text-[13px]">
          <Line label="Shipping" amount={shipping} />
          {charges.map((charge, i) => (
            <Line key={i} label={charge.reason?.trim() || "Extra charge"} amount={toNum(charge.amountKes)} />
          ))}
          <Line label="Delivery fee" amount={fee} />
          <div className="border-hairline flex items-center justify-between border-t pt-1.5 font-semibold">
            <dt>{settled ? "Paid" : "Customer owes"}</dt>
            <dd className="tabular">{formatKES(total)}</dd>
          </div>
        </dl>
      ) : (
        <>
          <DeliveryFee parcelId={parcel.id!} fee={fee} editable={FEE_EDITABLE.includes(status)} />
          <p className="text-text-faint text-[12px]">The amount due is set when the parcel lands in Kenya.</p>
        </>
      )}

      {status === "ON_HOLD" && <AddCharge parcelId={parcel.id!} />}
      {priced && <Payments parcelId={parcel.id!} />}
    </div>
  );
}

function Line({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-text-faint min-w-0 truncate">{label}</dt>
      <dd className="tabular shrink-0">{formatKES(amount)}</dd>
    </div>
  );
}

function DeliveryFee({ parcelId, fee, editable }: { parcelId: string; fee: number; editable: boolean }) {
  const refresh = useRefreshParcel(parcelId);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    const shillings = Number(value);
    if (value.trim() === "" || Number.isNaN(shillings) || shillings < 0) return setError("Enter the fee in KES, or 0 for none.");
    setBusy(true);
    setError(null);
    try {
      await api.ops.setDeliveryFee(parcelId, Math.round(shillings * 100));
      refresh();
      setEditing(false);
    } catch (err) {
      setError(err instanceof APIError ? err.message : "The fee was not saved. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 text-[13px]">
        <span className="text-text-faint">Delivery fee</span>
        <span className="flex items-center gap-2">
          <span className={cn("tabular", fee === 0 && "text-text-faint")}>{fee > 0 ? formatKES(fee) : "Not set"}</span>
          {editable && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setValue(fee > 0 ? String(Math.round(fee / 100)) : "");
                setEditing(true);
              }}
            >
              {fee > 0 ? "Change" : "Set fee"}
            </Button>
          )}
        </span>
      </div>
    );
  }

  return (
    <form className="space-y-1.5" onSubmit={save} noValidate>
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          type="number"
          inputMode="numeric"
          aria-label="Delivery fee in KES"
          placeholder="Delivery fee in KES"
          value={value}
          aria-invalid={!!error || undefined}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button type="submit" size="sm" loading={busy}>Save</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
      <FieldError>{error}</FieldError>
    </form>
  );
}

function AddCharge({ parcelId }: { parcelId: string }) {
  const refresh = useRefreshParcel(parcelId);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!(Number(amount) > 0)) return setError("Enter the charge in KES.");
    if (!reason.trim()) return setError("Say what the charge is for. The customer sees this.");
    setBusy(true);
    setError(null);
    try {
      await api.ops.addCharge(parcelId, { amountKes: Math.round(Number(amount) * 100), reason: reason.trim() });
      setAmount("");
      setReason("");
      refresh();
    } catch (err) {
      setError(err instanceof APIError ? err.message : "The charge was not added. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="bg-muted/40 space-y-2 rounded-md p-3" onSubmit={add} noValidate>
      <p className="text-[13px] font-medium">Add a charge</p>
      <div className="flex gap-2">
        <Input className="w-28 shrink-0" type="number" inputMode="numeric" aria-label="Charge in KES" placeholder="KES" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input aria-label="What the charge is for" placeholder="What it is for, shown to the customer" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <FieldError>{error}</FieldError>
      <Button type="submit" size="sm" variant="secondary" loading={busy}>Add to what the customer owes</Button>
    </form>
  );
}

function ago(iso?: string | null): string {
  if (!iso) return "";
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? "" : `${formatDistanceToNowStrict(date)} ago`;
}

function paymentSummary(payment: ITransaction): { text: string; tone: "ok" | "waiting" | "failed" } {
  if (payment.status === "SUCCESS") {
    const how = payment.source === "MANUAL" ? `marked paid${payment.paidBy ? ` by ${payment.paidBy}` : ""}` : `M-Pesa${payment.mpesaReceipt ? ` ${payment.mpesaReceipt}` : ""}`;
    return { text: `${formatKES(payment.amountKes)} received, ${how}`, tone: "ok" };
  }
  if (payment.status === "PENDING") return { text: `M-Pesa prompt for ${formatKES(payment.amountKes)} sent, waiting on the customer`, tone: "waiting" };
  return { text: `Payment failed: ${payment.resultDesc || "no reason given"}`, tone: "failed" };
}

function Payments({ parcelId }: { parcelId: string }) {
  const payments = useQuery({
    queryKey: ["ops", "transactions", "parcel", parcelId],
    queryFn: () => api.ops.listTransactions({ parcelId }),
  });
  const rows = payments.data ?? [];
  if (payments.isError) return <p className="text-destructive text-[12px]">Payment attempts did not load.</p>;
  if (rows.length === 0) return <p className="text-text-faint text-[12px]">No payment attempts yet.</p>;

  return (
    <ul className="space-y-1.5">
      {rows.slice(0, RECENT_PAYMENTS).map((payment) => {
        const { text, tone } = paymentSummary(payment);
        return (
          <li key={payment.id} className="flex items-start gap-2 text-[12px] leading-snug">
            <span
              className={cn(
                "mt-1.5 size-1.5 shrink-0 rounded-full",
                tone === "ok" ? "bg-success" : tone === "waiting" ? "bg-warning" : "bg-destructive",
              )}
            />
            <span className="min-w-0 flex-1">{text}</span>
            <span className="text-text-faint shrink-0 whitespace-nowrap" title={formatDate(payment.createdAt)}>
              {ago(payment.createdAt)}
            </span>
          </li>
        );
      })}
      {rows.length > RECENT_PAYMENTS && (
        <li className="text-text-faint text-[12px]">{rows.length - RECENT_PAYMENTS} earlier attempts are in Transactions.</li>
      )}
    </ul>
  );
}
